"use client";

import React, { useEffect, useState } from "react";
import { Loader2, Check } from "lucide-react";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import { REST_API_URL } from "../../api/config";

const API_URL = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };
  
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  
  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }
  
  return headers;
};

export default function ShowWorkersModal({ service, onClose, onUpdated }) {
  const [allWorkers, setAllWorkers] = useState([]);
  const [selectedWorkers, setSelectedWorkers] = useState(service.workers || []);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  useEffect(() => {
    if (!service) return;

    const fetchWorkers = async () => {
      try {
        setLoading(true);
        
        const headers = buildHeaders(token);
        
        const res = await fetch(`${API_URL}/auth/workers-list/`, {
          headers,
        });
        
        if (!res.ok) throw new Error("Failed to load workers");
        const data = await res.json();

        const validWorkers = data.filter(w => w.full_name && w.full_name.trim() !== "");
        setAllWorkers(validWorkers);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load workers");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkers();
  }, [service, token]);

  const toggleWorker = async (id) => {
    let updatedWorkers;
    if (selectedWorkers.includes(id)) {
      updatedWorkers = selectedWorkers.filter(w => w !== id);
    } else {
      updatedWorkers = [...selectedWorkers, id];
    }

    setSelectedWorkers(updatedWorkers);
    setLoading(true);

    try {
      const headers = buildHeaders(token);
      
      const res = await fetch(`${API_URL}/service/my-services/${service.id}/`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ ...service, workers: updatedWorkers }),
      });
      
      if (!res.ok) throw new Error("Failed to update workers");

      if (updatedWorkers.length === 0) {
        toast.success("Service turned OFF because no workers remain");

        onUpdated({ ...service, workers: updatedWorkers, on_off: false });
        return;
      }

      onUpdated(); 
    } catch (err) {
      console.error(err);
      toast.error("Failed to update workers");
    } finally {
      setLoading(false);
    }
  };

  if (!service) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="modal-backdrop"
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          key="modal-content"
          className="bg-white rounded-xl shadow-lg w-96 p-6 flex flex-col gap-4"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
        >
          <h2 className="text-lg font-normal">Workers for "{service.name}"</h2>

          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
            </div>
          ) : allWorkers.length > 0 ? (
            <div className="flex flex-col gap-2 max-h-72 overflow-y-auto">
              <AnimatePresence>
                {allWorkers.map((worker) => {
                  const isSelected = selectedWorkers.includes(worker.id);
                  return (
                    <motion.div
                      key={worker.id}
                      layout
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className={`flex items-center justify-between gap-3 p-2 rounded-lg cursor-pointer transition ${
                        isSelected ? "text-white border-blue-500" : "hover:bg-gray-100 border"
                      }`}
                      onClick={() => toggleWorker(worker.id)}
                    >
                      <div className="flex items-center gap-3">
                        <img
                          src={worker.image_url || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                          alt={worker.full_name || "No name"}
                          className="w-10 h-10 rounded-full object-cover border border-gray-200"
                        />
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-800">{worker.full_name}</span>
                          <span className="text-xs text-gray-500">{worker.worker_email}</span>
                        </div>
                      </div>
                      <div
                        className={`w-5 h-5 rounded-md border flex items-center justify-center transition ${
                          isSelected ? "bg-[#5863ED] border-blue-500" : "border-gray-300 bg-white"
                        }`}
                      >
                        {isSelected && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          ) : (
            <p>No workers assigned to this service.</p>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl bg-gray-100 text-black font-medium transition"
            >
              Close
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}